import { resolvedCurrentViewMode } from "./map-bridge-output.js";

const ZOOM_GAUGE_DISPLAY_MAX_TEXTURE_SCALE = 3;
const ZOOM_GAUGE_TEXTURE_SCALE_TICK_STEP = 0.5;
const ZOOM_GAUGE_SOURCE_TEXTURE_SCALE = 1;
const ZOOM_GAUGE_FLOAT_EPSILON = 1e-6;

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function finitePositiveNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function zoomTextureScaleFromSnapshot(snapshot) {
  const camera = snapshot?.view?.camera || {};
  const zoomBounds = snapshot?.view?.zoomBounds || {};
  const sourcePixelDistance = finitePositiveNumber(zoomBounds.sourcePixelDistance);
  const viewDistance = finitePositiveNumber(camera.distance) ?? finitePositiveNumber(camera.zoom);
  if (!sourcePixelDistance || !viewDistance) {
    return null;
  }
  return sourcePixelDistance / viewDistance;
}

function clampZoomTextureScale(scale) {
  if (!Number.isFinite(scale)) {
    return null;
  }
  return Math.min(
    ZOOM_GAUGE_DISPLAY_MAX_TEXTURE_SCALE,
    Math.max(ZOOM_GAUGE_TEXTURE_SCALE_TICK_STEP, scale),
  );
}

function snapZoomTextureScale(scale) {
  const clamped = clampZoomTextureScale(scale);
  if (!clamped) {
    return null;
  }
  return Math.min(
    ZOOM_GAUGE_DISPLAY_MAX_TEXTURE_SCALE,
    Math.max(
      ZOOM_GAUGE_TEXTURE_SCALE_TICK_STEP,
      Math.round(clamped / ZOOM_GAUGE_TEXTURE_SCALE_TICK_STEP)
        * ZOOM_GAUGE_TEXTURE_SCALE_TICK_STEP,
    ),
  );
}

export function viewDistanceRestoreCommandFromTextureScale(snapshot, textureScale) {
  const view = snapshot?.view || {};
  const sourcePixelDistance = finitePositiveNumber(view.zoomBounds?.sourcePixelDistance);
  const scale = finitePositiveNumber(textureScale);
  if (!sourcePixelDistance || !scale) {
    return null;
  }
  const distance = sourcePixelDistance / scale;
  const viewMode = resolvedCurrentViewMode(snapshot);
  const camera = {
    ...(isPlainObject(view.camera) ? view.camera : {}),
    distance,
    zoom: distance,
  };
  return {
    restoreView: {
      viewMode,
      camera,
    },
    setViewMode: viewMode,
  };
}

export function sourcePixelDistanceRestoreCommandFromSnapshot(snapshot) {
  return viewDistanceRestoreCommandFromTextureScale(snapshot, ZOOM_GAUGE_SOURCE_TEXTURE_SCALE);
}

function formatZoomTextureScale(scale) {
  if (!Number.isFinite(scale) || scale <= 0) {
    return "--";
  }
  if (scale >= 10) {
    return `${scale.toFixed(0)}x`;
  }
  return `${scale.toFixed(1)}x`;
}

export function zoomGaugePresentationFromSnapshot(snapshot) {
  const scale = zoomTextureScaleFromSnapshot(snapshot);
  if (!Number.isFinite(scale) || scale <= 0) {
    return null;
  }
  return {
    scale,
    formattedScale: formatZoomTextureScale(scale),
    progress: Math.min(1, Math.max(0, scale / ZOOM_GAUGE_DISPLAY_MAX_TEXTURE_SCALE)),
    ariaValue: Number(scale.toFixed(3)),
  };
}

export function createMapZoomGaugeController(shell, {
  readSnapshot = () => null,
  dispatchCommand = () => false,
} = {}) {
  const root = shell?.querySelector?.("#fishymap-zoom-gauge") || null;
  const trackElement = shell?.querySelector?.("#fishymap-zoom-gauge-track") || null;
  const valueElement = shell?.querySelector?.("#fishymap-zoom-gauge-value") || null;
  const ticksElement = shell?.querySelector?.("#fishymap-zoom-gauge-ticks") || null;
  if (!root || !trackElement || !valueElement || !ticksElement) {
    return Object.freeze({
      update() {
        return false;
      },
    });
  }

  const ownerDocument = root.ownerDocument || document;
  const ticks = [];
  for (
    let tick = ZOOM_GAUGE_TEXTURE_SCALE_TICK_STEP;
    tick <= ZOOM_GAUGE_DISPLAY_MAX_TEXTURE_SCALE + ZOOM_GAUGE_FLOAT_EPSILON;
    tick += ZOOM_GAUGE_TEXTURE_SCALE_TICK_STEP
  ) {
    const tickElement = ownerDocument.createElement("span");
    tickElement.className = "fishymap-zoom-gauge-tick";
    tickElement.style.setProperty("--x", String(tick / ZOOM_GAUGE_DISPLAY_MAX_TEXTURE_SCALE));
    tickElement.dataset.label = Number.isInteger(tick) ? `${tick.toFixed(0)}x` : `${tick}x`;
    if (Math.abs(tick - ZOOM_GAUGE_SOURCE_TEXTURE_SCALE) < ZOOM_GAUGE_FLOAT_EPSILON) {
      tickElement.dataset.major = "true";
    }
    ticks.push(tickElement);
  }
  ticksElement.replaceChildren(...ticks);

  let activePointerId = null;
  let lastDispatchedScale = null;
  let lastRenderedScale = Symbol("unrendered zoom scale");
  let suppressNextClick = false;

  function eventTextureScale(event) {
    const clientY = Number(event?.clientY);
    if (!Number.isFinite(clientY) || typeof trackElement.getBoundingClientRect !== "function") {
      return ZOOM_GAUGE_SOURCE_TEXTURE_SCALE;
    }
    const rect = trackElement.getBoundingClientRect();
    const height = finitePositiveNumber(rect?.height);
    if (!height) {
      return ZOOM_GAUGE_SOURCE_TEXTURE_SCALE;
    }
    const top = Number(rect.top || 0);
    const rawScale =
      ((top + height - clientY) / height) * ZOOM_GAUGE_DISPLAY_MAX_TEXTURE_SCALE;
    return snapZoomTextureScale(rawScale);
  }

  function currentTextureScale() {
    return zoomTextureScaleFromSnapshot(readSnapshot()) || ZOOM_GAUGE_SOURCE_TEXTURE_SCALE;
  }

  function dispatchTextureScale(scale) {
    const snappedScale = snapZoomTextureScale(scale);
    if (!snappedScale) {
      return false;
    }
    if (
      lastDispatchedScale != null &&
      Math.abs(snappedScale - lastDispatchedScale) < ZOOM_GAUGE_FLOAT_EPSILON
    ) {
      return false;
    }
    const command = viewDistanceRestoreCommandFromTextureScale(readSnapshot(), snappedScale);
    if (!command) {
      return false;
    }
    lastDispatchedScale = snappedScale;
    dispatchCommand(command);
    return true;
  }

  function dispatchTextureScaleFromEvent(event) {
    event?.preventDefault?.();
    return dispatchTextureScale(eventTextureScale(event));
  }

  root.addEventListener?.("pointerdown", (event) => {
    if (Number(event?.button || 0) !== 0) {
      return;
    }
    activePointerId = event.pointerId ?? 0;
    lastDispatchedScale = null;
    suppressNextClick = true;
    root.classList?.add?.("is-dragging");
    root.setPointerCapture?.(activePointerId);
    dispatchTextureScaleFromEvent(event);
  });
  root.addEventListener?.("pointermove", (event) => {
    if (activePointerId == null || (event.pointerId ?? 0) !== activePointerId) {
      return;
    }
    dispatchTextureScaleFromEvent(event);
  });
  function releasePointer(event) {
    if (activePointerId == null || (event?.pointerId ?? 0) !== activePointerId) {
      return;
    }
    root.releasePointerCapture?.(activePointerId);
    activePointerId = null;
    lastDispatchedScale = null;
    root.classList?.remove?.("is-dragging");
  }
  root.addEventListener?.("pointerup", releasePointer);
  root.addEventListener?.("pointercancel", releasePointer);
  root.addEventListener?.("click", (event) => {
    if (suppressNextClick) {
      suppressNextClick = false;
      return;
    }
    lastDispatchedScale = null;
    dispatchTextureScaleFromEvent(event);
  });
  root.addEventListener?.("keydown", (event) => {
    const key = event?.key;
    if (key === "ArrowLeft" || key === "ArrowDown") {
      event.preventDefault?.();
      lastDispatchedScale = null;
      dispatchTextureScale(currentTextureScale() - ZOOM_GAUGE_TEXTURE_SCALE_TICK_STEP);
      return;
    }
    if (key === "ArrowRight" || key === "ArrowUp") {
      event.preventDefault?.();
      lastDispatchedScale = null;
      dispatchTextureScale(currentTextureScale() + ZOOM_GAUGE_TEXTURE_SCALE_TICK_STEP);
      return;
    }
    if (key === " " || key === "Enter") {
      event.preventDefault?.();
      lastDispatchedScale = null;
      dispatchTextureScale(ZOOM_GAUGE_SOURCE_TEXTURE_SCALE);
    }
  });

  function update(snapshot) {
    const presentation = zoomGaugePresentationFromSnapshot(snapshot);
    const scale = presentation?.scale ?? null;
    if (Object.is(scale, lastRenderedScale)) {
      return false;
    }
    lastRenderedScale = scale;
    if (!presentation) {
      root.hidden = true;
      return true;
    }
    root.hidden = false;
    root.style.setProperty("--fishymap-zoom-gauge-progress", String(presentation.progress));
    root.dataset.zoomScale = presentation.formattedScale;
    root.setAttribute("aria-valuemin", String(ZOOM_GAUGE_TEXTURE_SCALE_TICK_STEP));
    root.setAttribute("aria-valuemax", String(ZOOM_GAUGE_DISPLAY_MAX_TEXTURE_SCALE));
    root.setAttribute("aria-valuenow", String(presentation.ariaValue));
    root.setAttribute("aria-valuetext", presentation.formattedScale);
    root.setAttribute("aria-orientation", "vertical");
    root.setAttribute("aria-label", `Map zoom ${presentation.formattedScale}`);
    valueElement.textContent = presentation.formattedScale;
    return true;
  }

  return Object.freeze({
    update,
  });
}

export function rotationRestoreCommandFromSnapshot(snapshot, yaw) {
  const view = snapshot?.view || {};
  const viewMode = resolvedCurrentViewMode(snapshot);
  const camera = {
    ...(isPlainObject(view.camera) ? view.camera : {}),
    yaw,
  };
  return {
    restoreView: {
      viewMode,
      camera,
    },
    setViewMode: viewMode,
  };
}

function formatCompassDegrees(yaw) {
  if (!Number.isFinite(yaw)) {
    return "0 deg";
  }
  return `${Math.round((((yaw * 180) / Math.PI) % 360 + 360) % 360)} deg`;
}

export function compassPresentationFromSnapshot(snapshot) {
  const yaw = finiteNumber(snapshot?.view?.camera?.yaw) ?? 0;
  const label = formatCompassDegrees(yaw);
  return {
    yaw,
    cssRotation: `${-yaw}rad`,
    label,
    resetLabel: `Reset map rotation (${label})`,
  };
}

export function createMapCompassController(shell, {
  readSnapshot = () => null,
  dispatchCommand = () => false,
} = {}) {
  const root = shell?.querySelector?.("#fishymap-compass-button") || null;
  const needle = shell?.querySelector?.("#fishymap-compass-needle") || null;
  const label = shell?.querySelector?.("#fishymap-compass-label") || null;
  if (!root || !needle) {
    return Object.freeze({
      update() {
        return false;
      },
    });
  }

  let lastRenderedYaw = Symbol("unrendered compass yaw");

  root.addEventListener?.("click", (event) => {
    event?.preventDefault?.();
    dispatchCommand(rotationRestoreCommandFromSnapshot(readSnapshot(), 0));
  });

  function update(snapshot) {
    const presentation = compassPresentationFromSnapshot(snapshot);
    if (Object.is(presentation.yaw, lastRenderedYaw)) {
      return false;
    }
    lastRenderedYaw = presentation.yaw;
    root.style.setProperty("--fishymap-compass-yaw", presentation.cssRotation);
    root.setAttribute("aria-label", presentation.resetLabel);
    root.setAttribute("data-fishy-tooltip", presentation.resetLabel);
    if (label) {
      label.textContent = presentation.label;
    }
    return true;
  }

  return Object.freeze({
    update,
  });
}
